/*
 * zdn-night.js
 *
 * The three behaviours the night showroom needs, and nothing else:
 *
 *  1. Showroom lights. Product cards start dim and come up as they scroll
 *     into view. The dim state is only ever applied by an inline script
 *     in the section that also sets a 3.5s failsafe, so if this file never
 *     loads the cards light themselves. window.zdnNight tells that
 *     failsafe this file arrived.
 *  2. Space filters on the range grid. Filtering hides cards in place; it
 *     never navigates, so the page does not jump and the back button
 *     still means "back".
 *  3. Spotlight videos. A product video plays only while it is on screen,
 *     only after it has been seen, and never for reduced-motion or
 *     data-saver visitors -- they keep the still photograph.
 *
 * Deferred, dependency-free, safe to run twice (theme editor reloads).
 */
(function () {
  window.zdnNight = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var saveData = navigator.connection && navigator.connection.saveData;
  var canObserve = 'IntersectionObserver' in window;

  /* ---------------------------------------------------------- lights */
  function initLights(root) {
    var grids = root.querySelectorAll('.zdn-dim');
    if (!grids.length) return;

    if (!canObserve || reduceMotion) {
      grids.forEach(function (grid) {
        grid.classList.remove('zdn-dim');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        var batch = 0;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var card = entry.target;
          card.style.setProperty('--zdn-i', String(batch));
          batch += 1;
          card.classList.add('is-lit');
          io.unobserve(card);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.2 }
    );

    grids.forEach(function (grid) {
      grid.querySelectorAll('.zdn-card').forEach(function (card) {
        if (!card.classList.contains('is-lit')) io.observe(card);
      });
    });
  }

  /* --------------------------------------------------------- filters */
  function initFilters(root) {
    root.querySelectorAll('[data-zdn-range]').forEach(function (range) {
      if (range.dataset.zdnReady) return;
      range.dataset.zdnReady = '1';

      var chips = range.querySelectorAll('[data-zdn-filter]');
      var cards = range.querySelectorAll('.zdn-card[data-spaces]');
      var status = range.querySelector('[data-zdn-status]');

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var want = chip.getAttribute('data-zdn-filter');
          var shown = 0;

          chips.forEach(function (other) {
            other.setAttribute('aria-pressed', other === chip ? 'true' : 'false');
          });

          cards.forEach(function (card) {
            var spaces = ' ' + (card.getAttribute('data-spaces') || '') + ' ';
            var match = want === 'all' || spaces.indexOf(' ' + want + ' ') !== -1;
            card.hidden = !match;
            if (match) {
              shown += 1;
              card.classList.add('is-lit');
            }
          });

          if (status) {
            var label = chip.getAttribute('data-zdn-label') || '';
            status.textContent = shown + (shown === 1 ? ' product' : ' products') + (label ? ' ' + label : '');
          }
        });
      });
    });
  }

  /* ---------------------------------------------------------- videos */
  function initVideos(root) {
    var videos = root.querySelectorAll('video[data-zdn-src]');
    if (!videos.length || reduceMotion || saveData || !canObserve) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            if (!video.getAttribute('src')) {
              video.muted = true;
              video.addEventListener(
                'canplay',
                function () {
                  video.classList.add('is-on');
                },
                { once: true }
              );
              video.setAttribute('src', video.getAttribute('data-zdn-src'));
            }
            var played = video.play();
            if (played && played.catch) {
              played.catch(function () {
                video.classList.remove('is-on');
              });
            }
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.45 }
    );

    videos.forEach(function (video) {
      io.observe(video);
    });
  }

  function init(root) {
    initLights(root);
    initFilters(root);
    initVideos(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();